/**
 * Content: everything staff edit in the console and the guest app and concierge read. Wire shapes
 * mirror the Drizzle rows (camelCase) minus internal columns — and never carry `venueId`: the venue is
 * bound server-side from the request (platform/venue). `XInput` is the create body; updates are
 * `XInput.partial()` (PATCH). Seed rows carry `PLACEHOLDER — ` in their description.
 */
import { z } from 'zod';
import { ClosureReason } from './availability.js';
import { CentsByWeekday, Currency, DateString, Email, HoursByWeekday, Id, Instant, NonNegativeCents, Percent, PhoneE164, Photo, PhotoInput, Slug, TimeHHmm, Timestamps, Weekday, } from './common.js';
// ── Venue (the tenant) ────────────────────────────────────────────────────
export const HexColor = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/, 'Expected #rrggbb or #rrggbbaa');
/**
 * Semantic colour overrides. Each key maps to a `--becca-color-*` custom property in `@becca/tokens`;
 * an omitted key keeps the default (Becca) theme.
 */
export const BrandColors = z
    .object({
    background: HexColor,
    surface: HexColor,
    text: HexColor,
    mutedText: HexColor,
    accent: HexColor,
    primary: HexColor,
    primaryText: HexColor,
    line: HexColor,
})
    .partial();
/** CSS font-family stacks; `stylesheetUrl` loads them (e.g. a Google Fonts css2 URL). */
export const BrandFonts = z
    .object({
    heading: z.string().max(300),
    body: z.string().max(300),
    serif: z.string().max(300),
    stylesheetUrl: z.url(),
})
    .partial();
const brandFields = {
    colors: BrandColors,
    fonts: BrandFonts,
    /** Default concierge name until the voice guide sets one. */
    conciergeName: z.string().max(80).nullable(),
    tagline: z.string().max(200).nullable(),
    websiteUrl: z.url().nullable(),
    instagramUrl: z.url().nullable(),
};
/** A venue's theme and identity; the guest app and console apply it at runtime over the default theme. */
export const Brand = z.object({ ...brandFields, logo: Photo.nullable() });
export const BrandInput = z.object({ ...brandFields, logo: PhotoInput.nullable() });
export const VenueStatus = z.enum(['active', 'onboarding', 'suspended']);
const venueFields = {
    name: z.string().min(1).max(120),
    description: z.string().max(4000),
    addressLine1: z.string().min(1).max(200),
    addressLine2: z.string().max(200).nullable(),
    city: z.string().min(1).max(100),
    state: z.string().min(2).max(50),
    postalCode: z.string().min(3).max(20),
    phone: PhoneE164,
    email: Email,
    hoursText: z.string().max(2000),
    /** Kosher supervision wording (Becca: RCBC), said once, proudly. Empty when not applicable. */
    kosherText: z.string().max(2000),
    /** IANA zone every event date and time of this venue is in. */
    timezone: z.string().min(1),
    /** For Shabbat and holiday times (@hebcal/core). */
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    /** Resy venue id for the ≤ event_min_guests reservation offer. */
    resyVenueId: z.string().max(40).nullable(),
    /** Where the ≤ event_min_guests offer sends a guest to book a table (Resy page). Null hides the link. */
    reservationUrl: z.string().url().max(500).nullable(),
};
/**
 * The tenant, as staff of that venue see it. `slug`, `guestHosts`, `embedKey` and `status` are managed
 * by Helix (no tenant self-service in phase 0) and are not in `VenueInput`.
 */
export const Venue = z.object({
    id: Id,
    slug: Slug,
    status: VenueStatus,
    /** Hostnames (with port locally) whose guest requests resolve to this venue. */
    guestHosts: z.array(z.string()),
    /** Public key the WordPress snippet sends as `X-Becca-Venue`. Not a secret. */
    embedKey: z.string(),
    /** Every `*_cents` amount at this venue. Managed by Helix. */
    currency: Currency,
    ...venueFields,
    brand: Brand,
    ...Timestamps,
});
/** `PUT /s/venue` — the active venue's profile and brand. */
export const VenueInput = z.object({ ...venueFields, brand: BrandInput });
// ── Rooms and space products ──────────────────────────────────────────────
const roomFields = {
    name: z.string().min(1).max(120),
    slug: Slug,
    seated: z.number().int().min(0).max(2000),
    standing: z.number().int().min(0).max(3000),
    /** Free text, e.g. "40 × 25 ft". */
    dimensions: z.string().max(120).nullable(),
    features: z.array(z.string().max(80)).max(30),
    description: z.string().max(4000),
    sort: z.number().int(),
    matterportModelId: z.string().max(80).nullable(),
    /** Not sellable before this date (the Garden Room). Null = sellable now. */
    sellableFrom: DateString.nullable(),
    active: z.boolean(),
};
/** A leaf room — the unit of availability. */
export const Room = z.object({
    id: Id,
    ...roomFields,
    photos: z.array(Photo),
    floorPlan: Photo.nullable(),
    ...Timestamps,
});
export const RoomInput = z.object({
    ...roomFields,
    photos: z.array(PhotoInput).max(40),
    floorPlan: PhotoInput.nullable(),
});
const spaceProductFields = {
    name: z.string().min(1).max(120),
    slug: Slug,
    /** Leaf rooms; booking the product books every one of them. */
    roomIds: z.array(Id).min(1),
    seated: z.number().int().min(0).max(2000),
    standing: z.number().int().min(0).max(3000),
    description: z.string().max(4000),
    /** One line, e.g. "intimate dinners up to 35". */
    bestFor: z.string().max(200).nullable(),
    hours: HoursByWeekday,
    setupMinutes: z.number().int().min(0).max(600),
    teardownMinutes: z.number().int().min(0).max(600),
    /** Minimum spend per weekday; 0 = none. */
    minSpendCents: CentsByWeekday,
    active: z.boolean(),
    sort: z.number().int(),
};
/** One sellable unit: a room, or a named combination of rooms (the buyout is one of them). */
export const SpaceProduct = z.object({
    id: Id,
    ...spaceProductFields,
    /** Product-specific photos; empty means the UI shows its rooms' photos. */
    photos: z.array(Photo),
    ...Timestamps,
});
export const SpaceProductInput = z.object({
    ...spaceProductFields,
    photos: z.array(PhotoInput).max(40),
});
/** How a product's capacity relates to the requested headcount. */
export const Fit = z.enum(['fits', 'too_small', 'too_large']);
// ── Packages and add-ons ──────────────────────────────────────────────────
export const MenuItem = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(1000).nullable(),
    /** e.g. "vegan", "gluten-free", "contains nuts". */
    dietary: z.array(z.string().max(40)).max(10),
});
export const Course = z.object({
    name: z.string().min(1).max(120),
    items: z.array(MenuItem).max(30),
});
export const PackagePriceKind = z.enum(['per_person', 'flat']);
const packageFields = {
    name: z.string().min(1).max(120),
    slug: Slug,
    description: z.string().max(4000),
    priceKind: PackagePriceKind,
    priceCents: NonNegativeCents,
    minGuests: z.number().int().min(1).nullable(),
    maxGuests: z.number().int().min(1).nullable(),
    courses: z.array(Course).max(20),
    included: z.array(z.string().max(200)).max(40),
    /** Empty = every product. */
    allowedProductIds: z.array(Id),
    /** Empty = every weekday. */
    allowedWeekdays: z.array(Weekday),
    active: z.boolean(),
    sort: z.number().int(),
};
export const Package = z.object({
    id: Id,
    ...packageFields,
    photos: z.array(Photo),
    ...Timestamps,
});
export const PackageInput = z.object({
    ...packageFields,
    photos: z.array(PhotoInput).max(20),
});
/**
 * How an add-on is priced. With an `AddonSelection.quantity` q and the plan's guest count g:
 * `per_person` = amount × g (q is 1) · `flat` = amount × q · `per_hour` = amount × q hours.
 */
export const AddonPricing = z.enum(['per_person', 'flat', 'per_hour']);
const addonFields = {
    name: z.string().min(1).max(120),
    slug: Slug,
    description: z.string().max(2000),
    pricing: AddonPricing,
    amountCents: NonNegativeCents,
    /** Empty = every package. */
    appliesToPackageIds: z.array(Id),
    active: z.boolean(),
    sort: z.number().int(),
};
export const Addon = z.object({ id: Id, ...addonFields, ...Timestamps });
export const AddonInput = z.object(addonFields);
// ── Pricing rules (versioned) ─────────────────────────────────────────────
export const DepositRule = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('pct'), value: Percent }),
    z.object({ kind: z.literal('flat'), valueCents: NonNegativeCents }),
]);
/** One installment after the deposit, due `daysBefore` the event. `remainder` = whatever is left. */
export const BalanceRule = z.object({
    label: z.string().min(1).max(120),
    daysBefore: z.number().int().min(0).max(730),
    amount: z.discriminatedUnion('kind', [
        z.object({ kind: z.literal('pct'), value: Percent }),
        z.object({ kind: z.literal('remainder') }),
    ]),
});
export const CardFeePassThrough = z.object({
    enabled: z.boolean(),
    pct: Percent,
    label: z.string().min(1).max(120),
    achExempt: z.boolean(),
});
export const PricingRules = z.object({
    serviceChargePct: Percent,
    taxPct: Percent,
    /** Whether tax is charged on the service charge too (NJ: yes). */
    taxAppliesToServiceCharge: z.boolean(),
    deposit: DepositRule,
    balanceSchedule: z.array(BalanceRule).max(10),
    finalHeadcountDaysBefore: z.number().int().min(0).max(365),
    /** Label of the min-spend shortfall line, e.g. "Room minimum". */
    minSpendShortfallLabel: z.string().min(1).max(120),
    cardFeePassThrough: CardFeePassThrough,
    /** How long a quote is valid, in days. */
    quoteValidDays: z.number().int().min(1).max(365),
});
export const PricingRule = z.object({
    id: Id,
    version: z.number().int().min(1),
    rules: PricingRules,
    note: z.string().max(500).nullable(),
    effectiveFrom: Instant,
    createdByStaffId: Id.nullable(),
    ...Timestamps,
});
/** `POST /s/pricing-rules` creates the next version. */
export const PricingRuleInput = z.object({
    rules: PricingRules,
    note: z.string().max(500).nullable().optional(),
});
// ── Closures ──────────────────────────────────────────────────────────────
export const ClosureRuleKind = z.enum(['weekly', 'jewish_holidays', 'shabbat_saturday_night']);
/** Weekly closure on these weekdays (Fri + Sat by default), for one product or (null) all. */
export const WeeklyClosureConfig = z.object({ weekdays: z.array(Weekday).min(1) });
/**
 * Holidays that close the venue, by @hebcal/core event description (e.g. "Yom Kippur", "Pesach I").
 * `closeErev`: also close from candle-lighting on the evening before.
 */
export const JewishHolidaysConfig = z.object({
    holidays: z.array(z.string().min(1)).max(60),
    closeErev: z.boolean(),
});
/** Opens Saturday at havdalah + `minutesAfterHavdalah` instead of the weekly closure. Off by default. */
export const ShabbatSaturdayNightConfig = z.object({
    minutesAfterHavdalah: z.number().int().min(0).max(240),
    /** Havdalah = sunset + this many minutes (hebcal `havdalahMins`; 50 is a common custom). */
    havdalahMins: z.number().int().min(0).max(120),
});
const closureRuleBase = {
    slug: Slug,
    name: z.string().min(1).max(120),
    description: z.string().max(2000),
    /** Null = every product. */
    productId: Id.nullable(),
    active: z.boolean(),
};
export const ClosureRuleInput = z.discriminatedUnion('kind', [
    z.object({ ...closureRuleBase, kind: z.literal('weekly'), config: WeeklyClosureConfig }),
    z.object({ ...closureRuleBase, kind: z.literal('jewish_holidays'), config: JewishHolidaysConfig }),
    z.object({
        ...closureRuleBase,
        kind: z.literal('shabbat_saturday_night'),
        config: ShabbatSaturdayNightConfig,
    }),
]);
export const ClosureRule = ClosureRuleInput.and(z.object({ id: Id, ...Timestamps }));
const closureFields = {
    startsAt: Instant,
    endsAt: Instant,
    reason: ClosureReason,
    label: z.string().min(1).max(200),
    /** Null = every product. */
    productId: Id.nullable(),
    /** False = staff overrode a materialised closure; the materialiser must not recreate it. */
    active: z.boolean(),
};
/** A concrete closed interval: materialised from a rule (`ruleId`) or a staff blackout (`ruleId` null). */
export const Closure = z.object({ id: Id, ...closureFields, ruleId: Id.nullable(), ...Timestamps });
export const ClosureInput = z.object(closureFields);
// ── Event types, knowledge, voice, templates, agreement ───────────────────
const eventTypeFields = {
    label: z.string().min(1).max(80),
    slug: Slug,
    sort: z.number().int(),
    active: z.boolean(),
};
export const EventType = z.object({ id: Id, ...eventTypeFields, ...Timestamps });
export const EventTypeInput = z.object(eventTypeFields);
export const KnowledgeAudience = z.enum(['guest', 'staff']);
const knowledgeFields = {
    slug: Slug,
    title: z.string().min(1).max(200),
    /** Markdown. */
    body: z.string().max(20_000),
    tags: z.array(z.string().max(40)).max(20),
    audience: KnowledgeAudience,
    active: z.boolean(),
};
export const KnowledgeArticle = z.object({ id: Id, ...knowledgeFields, ...Timestamps });
export const KnowledgeArticleInput = z.object(knowledgeFields);
const voiceGuideFields = {
    conciergeName: z.string().min(1).max(80),
    /** Greeting per scene key (`start`, `spaces`, `dates`, `packages`, `plan`, `talk`). */
    greetings: z.record(z.string(), z.string().max(500)),
    toneRules: z.array(z.string().max(300)).max(40),
    wordsToAvoid: z.array(z.string().max(80)).max(100),
    signOff: z.string().max(300),
};
/** One per venue. */
export const VoiceGuide = z.object({ id: Id, ...voiceGuideFields, ...Timestamps });
export const VoiceGuideInput = z.object(voiceGuideFields);
export const TemplateChannel = z.enum(['email', 'sms']);
/** Template keys seeded in phase 0. Staff may add others; code only sends the keys it knows. */
export const SEEDED_TEMPLATE_KEYS = [
    'instant_email',
    'hold_confirmed',
    'hold_expiring',
    'hold_expired',
    'plan_link',
    'inquiry_link',
    'question_answered',
    'staff_sign_in',
];
const templateFields = {
    key: z
        .string()
        .regex(/^[a-z][a-z0-9_]*$/)
        .max(80),
    name: z.string().min(1).max(120),
    description: z.string().max(2000),
    channel: TemplateChannel,
    /** `{{merge_field}}` allowed. Null for SMS. */
    subject: z.string().max(300).nullable(),
    /** Markdown with `{{merge_field}}`; values are HTML-escaped when rendered. */
    body: z.string().max(50_000),
    /** The merge fields this template expects, for the console's editor. */
    mergeFields: z.array(z.string().regex(/^[a-z][a-z0-9_]*$/)).max(60),
    active: z.boolean(),
};
export const Template = z.object({ id: Id, ...templateFields, ...Timestamps });
export const TemplateInput = z.object(templateFields);
/**
 * A version is created `draft` and becomes `approved` only through `POST /s/agreement-templates/:id/approve`
 * with the approver's name (Daniel's, 06 phase 3). Only an approved version is rendered for a guest;
 * otherwise `TEMPLATE_NOT_APPROVED`.
 */
export const AgreementTemplateStatus = z.enum(['draft', 'approved']);
/**
 * Versioned: `POST /s/agreement-templates` creates the next version. The approval fields (phase 3) are
 * optional on the wire only so phase-0 producers keep compiling; absent reads as `draft` (fail closed).
 * Test approval with `isAgreementTemplateApproved`, never with `status` alone.
 */
export const AgreementTemplate = z.object({
    id: Id,
    version: z.number().int().min(1),
    body: z.string().max(200_000),
    note: z.string().max(500).nullable(),
    effectiveFrom: Instant,
    createdByStaffId: Id.nullable(),
    status: AgreementTemplateStatus.optional(),
    /** Staff user who approved this version. */
    approvedBy: Id.nullish(),
    approvedAt: Instant.nullish(),
    /** The name typed on the approval, recorded as given. */
    approvedName: z.string().max(120).nullish(),
    ...Timestamps,
});
/** An agreement-template version that may be rendered for a guest; parsing a draft fails. */
export const ApprovedAgreementTemplate = AgreementTemplate.extend({
    status: z.literal('approved'),
    approvedBy: Id,
    approvedAt: Instant,
    approvedName: z.string().min(1).max(120),
});
export function isAgreementTemplateApproved(t) {
    return ApprovedAgreementTemplate.safeParse(t).success;
}
/** `POST /s/agreement-templates/:id/approve` */
export const ApproveAgreementTemplateBody = z.object({ name: z.string().trim().min(1).max(120) });
export const AgreementTemplateInput = z.object({
    body: z.string().min(1).max(200_000),
    note: z.string().max(500).nullable().optional(),
    effectiveFrom: Instant.optional(),
});
// ── Settings (one row per venue) ──────────────────────────────────────────────────
/**
 * What the public guest page shows (Daniel, 2026-10-28): `open` — the full walkthrough; `form_first` —
 * a short inquiry form, and the walkthrough only behind the personal link it sends.
 */
export const GuestFlow = z.enum(['open', 'form_first']);
export const SettingsValues = z.object({
    /** How long a guest hold lasts. */
    holdHours: z
        .number()
        .int()
        .min(1)
        .max(24 * 30),
    /** At or below this headcount it is a Resy reservation, not an event (02 §3.2). */
    eventMinGuests: z.number().int().min(1).max(200),
    /** Default event length offered when the guest has not chosen one. */
    defaultDurationMinutes: z.number().int().min(30).max(1440),
    /** No proactive outbound between these venue-local times (phase 2). */
    quietHours: z.object({ start: TimeHHmm, end: TimeHHmm }),
    /** Monthly AI spend cap (phase 1). */
    monthlyAiCapCents: z.number().int().min(0),
    tourBriefMinutesBefore: z.number().int().min(0).max(1440),
    missedCallText: z.boolean(),
    /** Lost leads are anonymised after this many months. */
    leadRetentionMonths: z.number().int().min(1).max(120),
    featureFlags: z.record(z.string(), z.boolean()),
    /** Absent in rows written before `0017_guest_flow`: parsed as `open`. */
    guestFlow: GuestFlow.default('open'),
});
export const Settings = z.object({ id: Id, values: SettingsValues, ...Timestamps });
export const SettingsInput = SettingsValues;
export const DEFAULT_SETTINGS = {
    holdHours: 72,
    eventMinGuests: 15,
    defaultDurationMinutes: 240,
    quietHours: { start: '21:00', end: '09:00' },
    monthlyAiCapCents: 10_000,
    tourBriefMinutesBefore: 30,
    missedCallText: false,
    leadRetentionMonths: 24,
    featureFlags: {},
    guestFlow: 'open',
};
//# sourceMappingURL=content.js.map